import { useState } from 'react';
import { useBoxes } from './hooks/useBoxes';
import { useSocket } from './hooks/useSocket';
import Header from './components/Header';
import BoxGrid from './components/BoxGrid';
import SettingsPanel from './components/SettingsPanel';
import SubscriptionsPanel from './components/SubscriptionsPanel';
import UsagePanel from './components/UsagePanel';
import LoginPage from './components/LoginPage';

interface TokenData {
  token: string;
  role: 'admin' | 'viewer';
}

function parseToken(raw: string): TokenData | null {
  if (!raw) return null;
  try {
    const payload = JSON.parse(atob(raw.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('fb_token');
      localStorage.removeItem('fb_role');
      return null;
    }
    return { token: raw, role: payload.role === 'admin' ? 'admin' : 'viewer' };
  } catch {
    localStorage.removeItem('fb_token');
    localStorage.removeItem('fb_role');
    return null;
  }
}

function getStoredSession(): TokenData | null {
  const raw = localStorage.getItem('fb_token');
  if (!raw) return null;
  return parseToken(raw);
}

function Dashboard({ token, role, onLogout }: { token: string; role: 'admin' | 'viewer'; onLogout: () => void }) {
  const { boxes, loading, error, lastUpdated, updateBox, refetch } = useBoxes(token);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [subscriptionsOpen, setSubscriptionsOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const isAdmin = role === 'admin';

  useSocket(updateBox, token, refetch);

  if (error === '401') {
    onLogout();
    return null;
  }

  return (
    <div className="min-h-screen bg-surface-bg font-exo text-ink-primary">
      <div className="fixed inset-0 bg-grid pointer-events-none" />
      <Header
        boxes={boxes}
        lastUpdated={lastUpdated}
        isAdmin={isAdmin}
        onSettingsOpen={() => setSettingsOpen(true)}
        onSubscriptionsOpen={() => setSubscriptionsOpen(true)}
        onUsageOpen={() => setUsageOpen(true)}
        onLogout={onLogout}
      />

      <main className="relative z-10 px-3 sm:px-5 py-4 sm:py-5">
        {loading && (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <div className="w-6 h-6 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
            <p className="text-ink-muted font-opensans text-sm">Verbinde mit Server…</p>
          </div>
        )}

        {error && error !== '401' && (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-red-light border border-brand-red-mid flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-brand-red font-opensans text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && <BoxGrid boxes={boxes} isAdmin={isAdmin} />}
      </main>

      {settingsOpen && isAdmin && <SettingsPanel onClose={() => setSettingsOpen(false)} token={token} />}
      {subscriptionsOpen && (
        <SubscriptionsPanel
          onClose={() => setSubscriptionsOpen(false)}
          token={token}
          boxes={boxes}
        />
      )}
      {usageOpen && (
        <UsagePanel onClose={() => setUsageOpen(false)} token={token} />
      )}
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<TokenData | null>(getStoredSession);

  function handleLogin(token: string, role: 'admin' | 'viewer') {
    localStorage.setItem('fb_token', token);
    setSession({ token, role });
  }

  function handleLogout() {
    localStorage.removeItem('fb_token');
    setSession(null);
  }

  if (!session) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <Dashboard token={session.token} role={session.role} onLogout={handleLogout} />;
}
